import React from "react";
import _findIndex from "lodash/findindex";
import useOnScreen from "../hooks/useOnScreen";
import useScrollSpy from "../hooks/useScrollSpy";
import SubmitBtn from "./submitButton.jsx";
import { AppBar, Tab, Tabs } from "@mui/material";
import Box from "@mui/material/Box";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import NavTabsSkeleton from "./Skeletons/NavTabsSkeleton.jsx";
import useDevice from "../hooks/useDevice.js";
import useParentScroll from "../hooks/useParentScroll.js";
import { scrollToElement } from "../utils/Utils.js";
import { colors } from "../components/common/mixins.js"

const tabTheme = createTheme({
  components: {
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: "#696781",
          fontSize: 16,
          padding: "16px",
          fontFamily: "pf_highway_sans_pro_lightRg",
          textTransform: "unset",
          "&.Mui-selected": {
            color: "#0D0C1C",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          height: "100%",
        },
        indicator: {
          height: "4px !important",
          backgroundColor: `${colors.primary} !important`,
        },
        scrollButtons: {
          color: "#696781",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "white",
        },
        positionFixed: {
          boxShadow: "8px 11px 11px -12px rgba(0,0,0,0.15)",
          "@media (min-width: 768px) and (max-width: 1296px)": {
            padding: "0 40px",
          },
          "@media (max-width: 767px)": {
            padding: "0",
          },
        },
        positionRelative: {
          boxShadow: "none",
          width: "auto",
          maxWidth: "1216px",
          margin: "0 16px 16px 16px",
        },
      },
    },
  },
});

const NavigationTabs = (props) => {
  const { isDesktop } = useDevice();
  const buttonVisible = useMediaQuery("(min-width:767px)");
  const appBarRef = React.useRef(null);
  const [appBarHeight, setAppBarHeight] = React.useState(0);

  // When embedded in a parent pym iframe, the iframe never scrolls (the parent does),
  // so MUI's position:fixed and the IntersectionObserver-based `headerVisible` don't
  // work. Drive stickiness from the parent's scroll offset instead.
  const { isEmbedded, parentScrollTop } = useParentScroll();
  const sentinelRef = React.useRef(null);
  const [naturalTop, setNaturalTop] = React.useState(0);

  let isEventPage = props.context === "Event";
  let info = props.pageInfoProp || {};
  let headerVisible = props.headerVisible;
  let items = [];

  // Measure AppBar total height including margins when it mounts or when loading changes
  React.useEffect(() => {
    if (appBarRef.current) {
      const rect = appBarRef.current.getBoundingClientRect();
      const styles = window.getComputedStyle(appBarRef.current);
      const marginBottom = parseFloat(styles.marginBottom);
      const totalHeight = rect.height + marginBottom;
      setAppBarHeight(totalHeight);
    }
    // The sentinel sits in normal flow (height 0). Its getBoundingClientRect().top is
    // its absolute position in the iframe content (the iframe never scrolls internally),
    // which is the same coordinate space as parentScrollTop.
    if (isEmbedded && sentinelRef.current) {
      setNaturalTop(sentinelRef.current.getBoundingClientRect().top);
    }
  }, [props.loading, isEmbedded]);

  // In embedded mode the bar "sticks" once the parent has scrolled past its resting spot.
  const stuck = isEmbedded && naturalTop > 0 && parentScrollTop >= naturalTop;

  // Also update height on window resize
  React.useEffect(() => {
    const handleResize = () => {
      if (appBarRef.current) {
        const rect = appBarRef.current.getBoundingClientRect();
        const styles = window.getComputedStyle(appBarRef.current);
        const marginBottom = parseFloat(styles.marginBottom);
        const totalHeight = rect.height + marginBottom;
        setAppBarHeight(totalHeight);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isEventPage || document.getElementById("r_descSummary"))
    items.push({
      hash: "r_summaryText",
      label: playdetailsv2_resources.LiteralResource10Text,
    });

  if (
    document.getElementById("r_discoverSection") &&
    (!isDesktop || !document.getElementById("r_descSummary"))
  )
    items.push({
      hash: "r_discoverSection",
      label: artistpage_resources.Discover,
    });

  if (document.getElementById("r_eventMedia"))
    items.push({
      hash: "r_eventMedia",
      label: playdetailsv2_resources.Gallery,
    });

  if (
    isEventPage &&
    !info.showModals &&
    !info.nullAdditionalInfo & !info.hasEventEnded
  )
    items.push({
      hash: "r_infoContainer",
      label: playdetailsv2_resources.UsefulInfo,
    });

  if (isEventPage && !info.hasEventEnded) {
    items.push({
      hash: "r_OrganizerContainerMain",
      label:
        info.producerCount > 1
          ? playdetailsv2_resources.Organizers
          : playdetailsv2_resources.Organizer,
    });
    items.push({
      hash: "workspace",
      label: playdetailsv2_resources.EventDates,
    });
  }

  if (document.getElementById("r_eventsSection"))
    items.push({
      hash: "r_eventsSection",
      label: artistpage_resources.Events,
    });

  let ticketSection = document.getElementById("r_ticketsSection");

  if (ticketSection)
    items.push({
      hash: "r_ticketsSection",
      label: artistpage_resources.Tickets,
    });

  if (document.getElementById("r_artistsSection"))
    items.push({
      hash: "r_artistsSection",
      label: artistpage_resources.Artists,
    });

  let tabs = items.map(({ label }, index) => (
    <Tab
      key={index}
      label={label}
      onClick={() =>
        scrollToElement(headerVisible, items[index].hash, props.context)
      }
    />
  ));

  let workspace = document.getElementById("workspace");

  const workspaceVisible = useOnScreen(workspace);

  const active = useScrollSpy({ items });
  const activeIndex = active ? _findIndex(items, ["hash", active]) : 0;

  if (props.loading) return <NavTabsSkeleton isEventPage={isEventPage} />;

  // When relative the bar sits in normal flow; when floating it is fixed (standalone)
  // or absolutely tracking the parent scroll offset (embedded in a pym iframe).
  const barRelative = isEmbedded ? !stuck : headerVisible;

  return (
    <ThemeProvider theme={tabTheme}>
      {/* Sentinel marks the AppBar's resting position in flow (embedded only) */}
      {isEmbedded && <div ref={sentinelRef} style={{ height: 0 }} />}
      {/* Placeholder div that maintains space when AppBar leaves normal flow */}
      {!barRelative && (
        <div
          style={{
            height: `${appBarHeight}px`,
            width: "100%",
            transition: "height 0.3s ease-in-out",
            overflow: "hidden",
          }}
        />
      )}
      <AppBar
        ref={appBarRef}
        id={"appbar"}
        position={
          isEmbedded
            ? stuck
              ? "fixed"
              : "relative"
            : headerVisible
            ? "relative"
            : "fixed"
        }
        sx={{
          transition:
            isEmbedded && stuck
              ? "box-shadow 0.3s ease-in-out"
              : "top 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
          // Inside the full-height iframe (scrollY always 0), position:fixed is relative
          // to the iframe viewport whose top === content top, so top:parentScrollTop pins
          // the bar to the parent's visible viewport. fixed also ignores positioned
          // ancestors, avoiding offset-parent issues that break position:absolute.
          ...(isEmbedded &&
            stuck && {
              top: `${parentScrollTop}px`,
              left: 0,
              right: 0,
              width: "100%",
              zIndex: 1100,
              boxShadow: "8px 11px 11px -12px rgba(0,0,0,0.15)",
            }),
          ...(barRelative &&
            (isEventPage
              ? { borderBottom: "1px solid #EDF1F6" }
              : { margin: "0 16px 0 16px" })),
        }}
      >
        <Box
          className="tabsContainer"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "1216px",
            padding: barRelative ? "0" : "0 16px",
          }}
        >
          <Tabs
            value={activeIndex > 0 ? activeIndex : 0}
            variant="scrollable"
            {...(barRelative &&
              !isEventPage && { sx: { borderBottom: "1px solid #EDF1F6" } })}
          >
            {tabs}
          </Tabs>
          {buttonVisible &&
            !workspaceVisible &&
            ((!headerVisible && !info.hasEventEnded) ||
              (!isEventPage && ticketSection)) && (
              <SubmitBtn
                buttonText={props.buttonText}
                isSticky={!headerVisible}
                toDoOnClick={() =>
                  scrollToElement(
                    headerVisible,
                    isEventPage ? "workspace" : "r_ticketsSection",
                    props.context
                  )
                }
                venueSeatsProp={props.venueSeatsProp}
              />
            )}
        </Box>
      </AppBar>
    </ThemeProvider>
  );
};

export default NavigationTabs;
